import React from "react";
import {
  Users,
  UserCheck,
  UserX,
  Building2,
  School,
  UserMinus,
} from "lucide-react";

const defaultStats = {
  total: 0,
  active_count: 0,
  inactive_count: 0,
  center_count: 0,
  school_count: 0,
  no_class_count: 0,
};

/**
 * Mini dashboard thống kê học viên (đồng bộ bộ lọc trang danh sách).
 * Mobile: 2 hàng × 3 cột; md+: một hàng 6 ô.
 */
const StudentMiniDashboard = ({
  stats = defaultStats,
  loading,
  exporting = false,
  onExportNoClass,
}) => {
  const s = { ...defaultStats, ...stats };

  const items = [
    {
      key: "total",
      label: "Tổng số",
      shortLabel: "Tổng",
      value: s.total,
      icon: Users,
      accent: "text-red-700",
      bg: "bg-red-50",
      border: "border-red-100",
    },
    {
      key: "active",
      label: "Hoạt động",
      shortLabel: "H.động",
      value: s.active_count,
      icon: UserCheck,
      accent: "text-emerald-700",
      bg: "bg-emerald-50",
      border: "border-emerald-100",
    },
    {
      key: "inactive",
      label: "Tạm ngưng",
      shortLabel: "T.ngưng",
      value: s.inactive_count,
      icon: UserX,
      accent: "text-slate-600",
      bg: "bg-slate-50",
      border: "border-slate-100",
    },
    {
      key: "center",
      label: "Trung tâm",
      shortLabel: "TTâm",
      value: s.center_count,
      icon: Building2,
      accent: "text-blue-700",
      bg: "bg-blue-50",
      border: "border-blue-100",
    },
    {
      key: "school",
      label: "Trường học",
      shortLabel: "Tr.học",
      value: s.school_count,
      icon: School,
      accent: "text-orange-700",
      bg: "bg-orange-50",
      border: "border-orange-100",
    },
    {
      key: "no_class",
      label: "Chưa có lớp",
      shortLabel: "Ch.lớp",
      value: s.no_class_count,
      icon: UserMinus,
      accent: "text-violet-700",
      bg: "bg-violet-50",
      border: "border-violet-100",
    },
  ];

  return (
    <section
      className="mb-4 sm:mb-6"
      aria-label="Thống kê học viên"
    >
      <div className="flex items-center justify-between gap-2 mb-2 sm:mb-3">
        <h2 className="text-[11px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider">
          Tổng quan
        </h2>
        {loading && (
          <span className="text-[10px] text-gray-400 animate-pulse">Đang cập nhật…</span>
        )}
      </div>

      {/* Mobile: 2 dòng × 3 cards */}
      <div className="md:hidden grid grid-cols-3 gap-2">
        {items.map(
          ({
            key,
            label,
            shortLabel,
            value,
            icon: Icon,
            accent,
            bg,
            border,
          }) => {
            const isNoClass = key === "no_class";
            const canExportNoClass = isNoClass && onExportNoClass && value > 0;
            const Wrapper = canExportNoClass ? "button" : "div";
            return (
            <Wrapper
              key={key}
              type={canExportNoClass ? "button" : undefined}
              disabled={canExportNoClass ? exporting : undefined}
              onClick={canExportNoClass ? onExportNoClass : undefined}
              title={
                canExportNoClass
                  ? exporting
                    ? "Đang tải file…"
                    : "Tải CSV danh sách học viên chưa có lớp"
                  : label
              }
              className={`
                rounded-xl border ${border} ${bg}
                px-2 py-2 min-h-[4rem] flex flex-col justify-center
                transition-shadow active:scale-[0.98]
                ${canExportNoClass ? "cursor-pointer hover:shadow-md text-left w-full disabled:opacity-60" : ""}
              `}
            >
              <div className="flex items-center gap-1 mb-0.5 min-w-0">
                <Icon className={`w-3 h-3 ${accent} shrink-0`} aria-hidden />
                <p
                  className="text-[9px] font-bold text-gray-500 uppercase tracking-wide leading-tight truncate"
                  title={label}
                >
                  {shortLabel}
                </p>
              </div>
              <p
                className={`text-base font-black tabular-nums ${accent} leading-none text-center`}
              >
                {loading ? "–" : value.toLocaleString("vi-VN")}
              </p>
              {canExportNoClass && (
                <p className="text-[8px] text-violet-600 font-bold mt-1 text-center leading-tight">
                  {exporting ? "Đang tải…" : "Bấm tải CSV"}
                </p>
              )}
            </Wrapper>
          );
          }
        )}
      </div>

      {/* Tablet+ : lưới đủ 6 cột */}
      <div className="hidden md:grid md:grid-cols-6 gap-3">
        {items.map(
          ({
            key,
            label,
            shortLabel,
            value,
            icon: Icon,
            accent,
            bg,
            border,
          }) => {
            const isNoClass = key === "no_class";
            const canExportNoClass = isNoClass && onExportNoClass && value > 0;
            const Wrapper = canExportNoClass ? "button" : "div";
            return (
            <Wrapper
              key={key}
              type={canExportNoClass ? "button" : undefined}
              disabled={canExportNoClass ? exporting : undefined}
              onClick={canExportNoClass ? onExportNoClass : undefined}
              title={
                canExportNoClass
                  ? exporting
                    ? "Đang tải file…"
                    : "Tải CSV danh sách học viên chưa có lớp"
                  : label
              }
              className={`
                rounded-xl border ${border} ${bg}
                px-3 py-3
                flex flex-row items-center gap-3
                transition-shadow hover:shadow-sm
                ${canExportNoClass ? "cursor-pointer hover:shadow-md text-left w-full disabled:opacity-60" : ""}
              `}
            >
              <div
                className={`shrink-0 w-9 h-9 rounded-lg ${bg} border ${border} flex items-center justify-center`}
              >
                <Icon className={`w-4 h-4 ${accent}`} aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p
                  className="text-[11px] font-bold text-gray-500 uppercase tracking-wide truncate"
                  title={label}
                >
                  {label}
                </p>
                <p className={`text-xl font-black tabular-nums ${accent}`}>
                  {loading ? "–" : value.toLocaleString("vi-VN")}
                </p>
                {canExportNoClass && (
                  <p className="text-[10px] text-violet-600 font-bold mt-0.5">
                    {exporting ? "Đang tải CSV…" : "Bấm để tải CSV"}
                  </p>
                )}
              </div>
            </Wrapper>
          );
          }
        )}
      </div>

      <p className="mt-2 text-[10px] text-gray-400 hidden md:block">
        Số liệu theo bộ lọc hiện tại (không phụ thuộc phân trang).
      </p>
    </section>
  );
};

export default StudentMiniDashboard;
